import { GRUPOS_COPA_2026, type GrupoCopaId } from '../data/copaMundo2026';
import { JOGOS_MATA_MATA, type OrigemLado } from '../data/mataMataCopa2026';
import {
  calcularClassificacaoGrupo,
  fasesGruposEncerrada,
  type PlacaresMap,
} from './copaCalculo';

export interface TerceiroColocado {
  timeId: string;
  nome: string;
  pts: number;
  sg: number;
  gp: number;
  grupo: GrupoCopaId;
}

/** 3º colocado atual de cada grupo (parcial ou completo). */
export function listarTerceirosAtuaisPorGrupo(placares: PlacaresMap): TerceiroColocado[] {
  return GRUPOS_COPA_2026.map((g) => {
    const cls = calcularClassificacaoGrupo(g.id, placares);
    const terceiro = cls[2];
    if (!terceiro) return null;
    return {
      timeId: terceiro.timeId,
      nome: terceiro.nome,
      pts: terceiro.pts,
      sg: terceiro.sg,
      gp: terceiro.gp,
      grupo: g.id,
    };
  }).filter((x): x is TerceiroColocado => Boolean(x));
}

/** Lista os 12 terceiros colocados ordenados (só quando os 72 jogos encerraram). */
export function listarTerceirosColocadosOrdenados(
  resultadosReais: PlacaresMap
): TerceiroColocado[] {
  if (!fasesGruposEncerrada(resultadosReais)) return [];

  const arr = listarTerceirosAtuaisPorGrupo(resultadosReais);
  arr.sort((a, b) => {
    if (b.pts !== a.pts) return b.pts - a.pts;
    if (b.sg !== a.sg) return b.sg - a.sg;
    if (b.gp !== a.gp) return b.gp - a.gp;
    return a.nome.localeCompare(b.nome, 'pt-BR');
  });
  return arr;
}

export interface InfoSlotMelhor3o {
  slot: number;
  gruposElegiveis: GrupoCopaId[];
  jogoFifa: number;
  jogoId: string;
  adversarioGrupo?: GrupoCopaId;
  adversarioPosicao?: 1 | 2;
}

/** Metadados de cada slot M3-1..8 (jogo FIFA e adversário fixo do chaveamento). */
export function listarInfoSlotsMelhor3o(): InfoSlotMelhor3o[] {
  const out: InfoSlotMelhor3o[] = [];
  JOGOS_MATA_MATA.filter((j) => j.fase === '16avos').forEach((j) => {
    const par: [OrigemLado, OrigemLado] = [j.ladoA, j.ladoB];
    par.forEach((lado, idx) => {
      if (lado.tipo !== 'melhor-3o') return;
      const outro = par[1 - idx];
      const info: InfoSlotMelhor3o = {
        slot: lado.slot,
        gruposElegiveis: lado.gruposElegiveis,
        jogoFifa: j.numeroFifa,
        jogoId: j.id,
      };
      if (outro.tipo === 'pos-grupo') {
        info.adversarioGrupo = outro.grupo;
        info.adversarioPosicao = outro.posicao;
      }
      out.push(info);
    });
  });
  return out.sort((a, b) => a.slot - b.slot);
}

/** Opções para o dropdown de um slot M3 (3ºs atuais + todos os times dos grupos elegíveis). */
export function opcoesParaSlotMelhor3o(slot: number, placares: PlacaresMap): TerceiroColocado[] {
  const info = listarInfoSlotsMelhor3o().find((s) => s.slot === slot);
  if (!info) return [];
  const elegiveis = new Set(info.gruposElegiveis);
  const byId = new Map<string, TerceiroColocado>();

  listarTerceirosAtuaisPorGrupo(placares)
    .filter((t) => elegiveis.has(t.grupo))
    .forEach((t) => byId.set(t.timeId, t));

  GRUPOS_COPA_2026.filter((g) => elegiveis.has(g.id)).forEach((g) => {
    g.times.forEach((tm) => {
      if (!byId.has(tm.id)) {
        byId.set(tm.id, {
          timeId: tm.id,
          nome: tm.nome,
          pts: 0,
          sg: 0,
          gp: 0,
          grupo: g.id,
        });
      }
    });
  });

  return [...byId.values()].sort((a, b) => {
    if (a.grupo !== b.grupo) return a.grupo.localeCompare(b.grupo);
    return a.nome.localeCompare(b.nome, 'pt-BR');
  });
}

function extrairSlotsMelhor3o(): { slot: number; gruposElegiveis: GrupoCopaId[] }[] {
  const map = new Map<number, GrupoCopaId[]>();
  JOGOS_MATA_MATA.filter((j) => j.fase === '16avos').forEach((j) => {
    [j.ladoA, j.ladoB].forEach((lado) => {
      if (lado.tipo === 'melhor-3o') map.set(lado.slot, lado.gruposElegiveis);
    });
  });
  return [1, 2, 3, 4, 5, 6, 7, 8].map((slot) => ({
    slot,
    gruposElegiveis: map.get(slot) || [],
  }));
}

function resolverAtribuicaoSlots(
  slots: { slot: number; gruposElegiveis: GrupoCopaId[] }[],
  gruposClassificados: Set<GrupoCopaId>,
  terceirosPorGrupo: Map<GrupoCopaId, TerceiroColocado>,
  idx: number,
  usados: Set<GrupoCopaId>,
  out: Record<number, string>
): boolean {
  if (idx >= slots.length) return true;
  const { slot, gruposElegiveis } = slots[idx];
  for (const g of gruposElegiveis) {
    if (!gruposClassificados.has(g) || usados.has(g)) continue;
    const t = terceirosPorGrupo.get(g);
    if (!t) continue;
    usados.add(g);
    out[slot] = t.timeId;
    if (
      resolverAtribuicaoSlots(
        slots,
        gruposClassificados,
        terceirosPorGrupo,
        idx + 1,
        usados,
        out
      )
    ) {
      return true;
    }
    usados.delete(g);
    delete out[slot];
  }
  return false;
}

/**
 * Atribui os 8 melhores 3ºs aos slots M3-1..M3-8 (critério FIFA + grupos elegíveis).
 * Retorna null se a fase de grupos ainda não encerrou.
 */
export function atribuirMelhoresTerceirosAutomatico(
  resultadosReais: PlacaresMap
): Record<number, string> | null {
  if (!fasesGruposEncerrada(resultadosReais)) return null;

  const terceiros = listarTerceirosColocadosOrdenados(resultadosReais);
  if (terceiros.length < 8) return null;

  const classificados = terceiros.slice(0, 8);
  const gruposClassificados = new Set(classificados.map((t) => t.grupo));
  const terceirosPorGrupo = new Map(terceiros.map((t) => [t.grupo, t]));
  const slots = extrairSlotsMelhor3o();
  const out: Record<number, string> = {};
  const ok = resolverAtribuicaoSlots(
    slots,
    gruposClassificados,
    terceirosPorGrupo,
    0,
    new Set(),
    out
  );
  if (!ok || Object.keys(out).length !== 8) return null;
  return out;
}
